import type { Timezones } from '@hero/schema';
import dayjs, { type Dayjs } from 'dayjs';
import { CONFIG } from '../config';
import { getDateInTimezone, getDateNowInTimezone, formatDateMain } from '../date/date';

export interface PickupDateRange {
  from: Date;
  to: Date;
}

export interface PickupTimeSlot {
  value: string;
  label: string;
  fromTime: string;
  toTime: string;
  from: Date;
  to: Date;
  isNow: boolean;
}

export interface GetDefaultPickupDateRangeOptions {
  /** IANA timezone — defaults to platform default (`Asia/Riyadh`). */
  timezone?: Timezones;
  /** Override clock reading (tests). */
  now?: Date;
}

const PREDEFINED_SLOT_HOURS = [7, 9, 11, 13, 15, 17, 19, 20];

const getNowInTimezone = (timezone: Timezones, now?: Date): Dayjs =>
  now ? getDateInTimezone(now, timezone) : getDateInTimezone(getDateNowInTimezone(timezone), timezone);

const getNowSlotStartHour = (now: Dayjs): number | null => {
  const currentHour = now.hour();
  const currentMinutes = now.minute();
  const nextHour = currentMinutes > 0 ? currentHour + 1 : currentHour;
  return PREDEFINED_SLOT_HOURS.find(h => h >= nextHour) ?? null;
};

/**
 * True when `from` is today's next available predefined pickup slot.
 */
export const isNowPickupSlot = (
  from: Date | string,
  options: GetDefaultPickupDateRangeOptions = {},
): boolean => {
  const timezone = options.timezone ?? CONFIG.time.defaultTimezone;
  const now = getNowInTimezone(timezone, options.now);
  const fromDay = getDateInTimezone(from, timezone);
  if (!fromDay.isSame(now, 'day')) return false;
  const startHour = getNowSlotStartHour(now);
  if (startHour == null) return false;
  return fromDay.hour() === startHour;
};

/**
 * Generate 2-hour pickup slots for a calendar day in the given timezone.
 * Today: only future slots; future dates: full predefined set (7 AM–10 PM).
 */
export const generatePickupTimeSlots = (
  selectedDate: Date | string,
  options: GetDefaultPickupDateRangeOptions = {},
): PickupTimeSlot[] => {
  const timezone = options.timezone ?? CONFIG.time.defaultTimezone;
  const currentTime = getNowInTimezone(timezone, options.now);
  const day = getDateInTimezone(selectedDate, timezone).startOf('day');
  const isToday = day.isSame(currentTime, 'day');
  const nowStartHour = isToday ? getNowSlotStartHour(currentTime) : null;

  const slotHours = isToday
    ? PREDEFINED_SLOT_HOURS.filter(startHour => {
        const currentHour = currentTime.hour();
        const currentMinutes = currentTime.minute();
        const nextHour = currentMinutes > 0 ? currentHour + 1 : currentHour;
        return startHour >= nextHour;
      })
    : PREDEFINED_SLOT_HOURS;

  const slots: PickupTimeSlot[] = [];

  for (const startHour of slotHours) {
    const from = day.hour(startHour).minute(0).second(0).millisecond(0);
    const to = from.add(2, 'hour');

    const fromHour12 = from.hour() % 12 || 12;
    const toHour12 = to.hour() % 12 || 12;
    const fromAmPm = from.hour() < 12 ? 'AM' : 'PM';
    const toAmPm = to.hour() < 12 ? 'AM' : 'PM';

    slots.push({
      value: `slot_${startHour}_${startHour + 2}`,
      label: `${fromHour12}:00 ${fromAmPm} - ${toHour12}:00 ${toAmPm}`,
      fromTime: `${startHour.toString().padStart(2, '0')}:00`,
      toTime: `${(startHour + 2).toString().padStart(2, '0')}:00`,
      from: from.toDate(),
      to: to.toDate(),
      isNow: startHour === nowStartHour,
    });
  }

  return slots;
};

const getDefaultSlotValue = (
  selectedDate: Dayjs,
  availableSlots: PickupTimeSlot[],
): string | null => {
  if (availableSlots.length === 0) return null;
  const recommendedSlot = availableSlots.find(slot => {
    const h = getDateInTimezone(slot.from).hour();
    return h === 9;
  });
  if (recommendedSlot) return recommendedSlot.value;
  return availableSlots[0]?.value ?? null;
};

/**
 * Recommended pickup window for new orders — same rules as the publish-order
 * page (`getAutoDateRange` in hooks-shared). Uses org/platform timezone so
 * backend agent defaults match carrier-dashboard UX.
 */
export const getDefaultPickupDateRange = (
  options: GetDefaultPickupDateRangeOptions = {},
): PickupDateRange => {
  const timezone = options.timezone ?? CONFIG.time.defaultTimezone;
  const now = getNowInTimezone(timezone, options.now);
  const currentHour = now.hour();

  const defaultDate =
    currentHour >= 20 ? now.add(1, 'day').startOf('day') : now.startOf('day');

  const availableSlots = generatePickupTimeSlots(defaultDate.toDate(), options);
  const defaultSlot = getDefaultSlotValue(defaultDate, availableSlots);

  if (defaultSlot && availableSlots.length > 0) {
    const slot = availableSlots.find(s => s.value === defaultSlot);
    if (slot) {
      return { from: slot.from, to: slot.to };
    }
  }

  const tomorrow = now.add(1, 'day').hour(9).minute(0).second(0).millisecond(0);
  return {
    from: tomorrow.toDate(),
    to: tomorrow.add(2, 'hour').toDate(),
  };
};

/** ISO `{ from, to }` pair for agent tools and APIs. */
export const getDefaultPickupDateRangeIso = (
  options: GetDefaultPickupDateRangeOptions = {},
): { from: string; to: string; timezone: Timezones } => {
  const timezone = options.timezone ?? CONFIG.time.defaultTimezone;
  const range = getDefaultPickupDateRange(options);
  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    timezone,
  };
};

/**
 * Human-readable pickup window string (e.g. `"Today 9:00 AM – 11:00 AM"`)
 * resolved in the caller's wall-clock. Shared between the agent's
 * `get_default_pickup_date` tool and any UI surface that needs to
 * paraphrase a pickup window — keeps wording consistent.
 *
 * Returns an empty string when either bound is missing/invalid so
 * call sites can `{label && <…>}` cleanly.
 */
export const formatPickupWindow = (
  range:
    | { from?: Date | string | null; to?: Date | string | null }
    | null
    | undefined,
  timezone: Timezones = CONFIG.time.defaultTimezone,
): string => {
  if (!range) return '';
  const from = formatDateMain({
    date: range.from,
    format: 'relative_without_time',
    timezone,
  });
  const startTime = formatDateMain({
    date: range.from,
    format: 'time',
    timezone,
  });
  const endTime = formatDateMain({
    date: range.to,
    format: 'time',
    timezone,
  });
  if (!from || !startTime || !endTime) return '';
  return `${from} ${startTime} – ${endTime}`;
};

/**
 * ISO `{ from, to }` pair + `timezone` + human-readable `label`
 * describing the default pickup window. The LLM is instructed to
 * quote `label` to the user and pass `iso` back into staging tools —
 * solves the "ISO interpreted as UTC by the model" bug that
 * surfaced as "6 AM" in chat for a 9 AM Riyadh window.
 *
 * Sample outputs (Asia/Riyadh):
 *   { iso: { from: '…T06:00Z', to: '…T08:00Z' },
 *     timezone: 'Asia/Riyadh',
 *     label: 'Today 9:00 AM – 11:00 AM' }
 */
export const getDefaultPickupDateRangeWithLabel = (
  options: GetDefaultPickupDateRangeOptions = {},
): {
  iso: { from: string; to: string };
  label: string;
  timezone: Timezones;
} => {
  const timezone = options.timezone ?? CONFIG.time.defaultTimezone;
  const range = getDefaultPickupDateRange(options);
  return {
    iso: { from: range.from.toISOString(), to: range.to.toISOString() },
    label: formatPickupWindow(range, timezone),
    timezone,
  };
};
